import { CalendarOutlined, FilePdfOutlined } from '@ant-design/icons'
import { Flex, Image, Modal, Tag, Typography } from 'antd'
import { PropertyActivityItem } from 'Modules'

import MarkdownEditor from 'component/markdown-editor/markdown-editor'
import { getActivityTypeName } from 'constants/property-activities'
import { formatShortDate } from 'utils/format-time'

const { Text, Title } = Typography

interface ViewActivityModalProps {
  open: boolean
  activity: PropertyActivityItem | null
  onClose: () => void
}

const getFileNameFromUrl = (url: string): string => {
  try {
    const u = new URL(url)
    return decodeURIComponent(u.pathname.split('/').pop() || 'file')
  } catch {
    return url.split('/').pop()?.split('?')[0] || 'file'
  }
}

export const ViewActivityModal = ({ open, activity, onClose }: ViewActivityModalProps) => {
  const imageUrls = activity?.images ?? []
  const pdfUrls = activity?.documents ?? []

  return (
    <Modal
      open={open}
      title="Activity Details"
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      width={600}
    >
      {activity && (
        <Flex vertical gap={16} style={{ marginTop: 16 }}>
          <Flex gap={8} align="center" wrap="wrap">
            <Title level={4} style={{ margin: 0 }}>
              {activity.title}
            </Title>
            <Tag color="default" className="property-activities__status-tag is-published">
              Published
            </Tag>
          </Flex>

          {getActivityTypeName(activity) && (
            <Tag color="blue" style={{ width: 'fit-content' }}>
              {getActivityTypeName(activity)}
            </Tag>
          )}

          <Flex gap={6} align="center">
            <CalendarOutlined style={{ color: '#9ca3af' }} />
            <Text type="secondary">{formatShortDate(activity.timeline)}</Text>
          </Flex>

          <div>
            <Text strong>Description</Text>
            <div style={{ marginTop: 4 }}>
              <MarkdownEditor disabled value={activity.description} placeholder="No description" />
            </div>
          </div>

          {imageUrls.length > 0 && (
            <div>
              <Text strong>Images ({imageUrls.length})</Text>
              <Flex gap={8} wrap="wrap" style={{ marginTop: 8 }}>
                <Image.PreviewGroup>
                  {imageUrls.map((url, idx) => (
                    <Image
                      key={idx}
                      src={url}
                      width={120}
                      height={90}
                      style={{ objectFit: 'cover', borderRadius: 8 }}
                    />
                  ))}
                </Image.PreviewGroup>
              </Flex>
            </div>
          )}

          {pdfUrls.length > 0 && (
            <div>
              <Text strong>Documents ({pdfUrls.length})</Text>
              <Flex gap={8} wrap="wrap" style={{ marginTop: 8 }}>
                {pdfUrls.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="property-activities__image-thumb is-pdf"
                    title={getFileNameFromUrl(url)}
                  >
                    <FilePdfOutlined style={{ fontSize: 28, color: '#ef4444' }} />
                    <span className="property-activities__pdf-thumb-name">
                      {getFileNameFromUrl(url)}
                    </span>
                  </a>
                ))}
              </Flex>
            </div>
          )}
        </Flex>
      )}
    </Modal>
  )
}
